import { useEffect, useRef } from "react";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import LayoutWrapper from "@/components/Layout/LayoutWrapper";
import Table from "@/components/Table/Table";
import useTutorialMode from "@/components/TutorialMode/hooks/useTutorialMode";
import useResponse from "@/shared/hooks/useResponse";
import { useVehiclesTableStore } from "@/stores/useTableStore";

import DownloadMonitoringDialog from "../../components/DetailsSection/components/DetailsContent/components/DownloadMonitoringDialog/DownloadMonitoringDialog";
import VehiclesHeaderActions from "../../components/PageHeader/actions/pageHeaderActions/VehiclesHeaderActions/VehiclesHeaderActions";
import EditVehicleDialog from "../../components/Table/actions/dialogs/Vehicles/EditVehicleDialog/EditVehicleDialog";
import useVehicleInlineActions from "../../components/Table/actions/inlineActions/hooks/useVehicleInlineActions";
import useTableInlineActions from "../../components/Table/hooks/useTableInlineActions";
import { USER_PERMISSIONS, USER_ROLES_RAW_DATA } from "../../roles/types/enums";
import { ROUTES, TABLE_NAMES, VEHICLES_ACTIONS } from "../../shared";

const Vehicles = () => {
  const { getData } = useAxiosPrivate();
  const { handleResponse } = useResponse();

  const {
    inlineActions,
    rowData,
    isEditOpen,
    isDialogOpen,
    closeEditDialog,
    handleCloseDownloadStatusDialog
  } = useVehicleInlineActions();

  const { handleTutorialInVehiclesTable, isTutorialActive } = useTutorialMode();

  const { fetchData, resetInitialLoading, setIsCheckable, setInlineActions } =
    useVehiclesTableStore();

  const onlyDownloadMonitoring = inlineActions.slice(0, 1);

  const { tableInlineActions, allowedToManageData: allowedToManageVehicles } =
    useTableInlineActions(
      inlineActions,
      USER_PERMISSIONS.ManageVehicles,
      USER_ROLES_RAW_DATA.TeltonikaSupport,
      onlyDownloadMonitoring
    );

  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      resetInitialLoading();
      hasFetched.current = true;
      handlePageLoad();
    }
  }, []);

  useEffect(() => {
    updateVehiclesActions();
  }, [tableInlineActions]);

  const updateVehiclesActions = () => {
    setInlineActions(tableInlineActions);
    setIsCheckable(allowedToManageVehicles);
  };

  const handlePageLoad = async () => {
    await fetchData(getData, handleResponse);
    if (isTutorialActive) {
      updateVehiclesActions();
      const dataLength = useVehiclesTableStore.getState().data.length;
      handleTutorialInVehiclesTable(dataLength);
    }
  };

  return (
    <>
      <LayoutWrapper
        showDetailsSection
        tableName={TABLE_NAMES.Vehicles}
        route={ROUTES.Vehicles}
      >
        {allowedToManageVehicles && <VehiclesHeaderActions />}
      </LayoutWrapper>
      <Table
        hasDetails
        hideFilters={false}
        hasTableView
        route={ROUTES.Vehicles}
      />

      {/* inline action dialog */}
      {rowData && (
        <>
          {isDialogOpen[VEHICLES_ACTIONS.DownloadMonitoring] && (
            <DownloadMonitoringDialog
              open
              onClose={handleCloseDownloadStatusDialog}
              rowData={rowData}
            />
          )}
          {isEditOpen && (
            <EditVehicleDialog
              isOpen
              onClose={closeEditDialog}
              rowData={rowData}
            />
          )}
        </>
      )}
    </>
  );
};

export default Vehicles;
